import * as React from 'react';
import { AdminCampaignRun } from '@app/Campaigns/types';
import { relativeTime, formatDateTime } from '@app/Screens/format';
import { colors } from '@app/components/designTokens';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '13px 16px',
  fontSize: 14,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle'
};

/** Color a deadline by urgency: past = red, within 30 min = amber, else muted. */
function deadlineStyle(iso: string | null): React.CSSProperties {
  if (!iso) return { color: colors.placeholder };
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return { color: colors.placeholder };
  if (ms <= 0) return { color: colors.danger, fontWeight: 700 };
  if (ms <= 30 * 60 * 1000) return { color: colors.warning, fontWeight: 700 };
  return { color: colors.body };
}

export const ApprovalQueueTable: React.FunctionComponent<{
  runs: AdminCampaignRun[];
  loading: boolean;
  onSelect: (campaignId: number) => void;
}> = ({ runs, loading, onSelect }) => {
  const [hovered, setHovered] = React.useState<number | null>(null);

  if (loading) {
    return <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>Loading approval queue…</div>;
  }
  if (runs.length === 0) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: '#047857', fontWeight: 600 }}>
        No runs awaiting driver approval. 🎉
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
        <thead>
          <tr>
            <th style={th}>Campaign</th>
            <th style={th}>Assigned driver</th>
            <th style={th}>Slot start</th>
            <th style={th}>Approval deadline</th>
            <th style={th}>Booked</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(r => (
            <tr
              key={r.runId}
              onClick={() => r.campaignId != null && onSelect(r.campaignId)}
              onMouseEnter={() => setHovered(r.runId)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: r.campaignId != null ? 'pointer' : 'default', background: hovered === r.runId ? '#f8fafc' : 'transparent' }}
            >
              <td style={td}>
                <div style={{ fontWeight: 600 }}>{r.campaignName || `Campaign #${r.campaignId ?? '?'}`}</div>
                <div style={{ fontSize: 12, color: colors.subtle }}>run #{r.runId}</div>
              </td>
              <td style={td}>{r.driverName || <span style={{ color: colors.placeholder }}>unassigned</span>}</td>
              <td style={td} title={formatDateTime(r.scheduledStartAt)}>{formatDateTime(r.scheduledStartAt)}</td>
              <td style={{ ...td, ...deadlineStyle(r.approvalDeadline) }} title={formatDateTime(r.approvalDeadline)}>
                {r.approvalDeadline ? relativeTime(r.approvalDeadline) : '—'}
              </td>
              <td style={td}>{relativeTime(r.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
